// symptomDiary.js — pure logic for the chronic-condition diary + VA evidence
// tracker upgrade (Jun 2026). No React, no network — fully Jest-testable and
// shared by the Symptom Tracker view, the Insights panel, and both PDF exports.

// ---- Vocabularies --------------------------------------------------------------

export const BODY_SIDES = ["Left", "Right", "Both", "Bilateral", "N/A"];

export const TREATMENT_OPTIONS = [
  "Physical therapy", "Provider visit", "Therapy / counseling", "Chiropractor", "Injection",
  "Brace / support", "Cane / crutches", "CPAP", "TENS unit",
  "Hot / cold pack", "Rest", "Home exercise / stretching", "ER / urgent care",
];

export const ASSISTIVE_DEVICE_OPTIONS = [
  "Brace", "Cane", "Crutches", "Walker", "Wheelchair", "CPAP",
  "Hearing aids", "TENS unit", "Orthotics", "Compression sleeve",
];

export const MISSED_OPTIONS = [
  "Work", "Physical therapy", "Duty", "School", "Appointment", "Family event", "Social event",
];

export const TAG_OPTIONS = [
  "C&P prep", "Evidence-ready", "Flare-up", "Appointment",
  "Medication issue", "Sleep", "Work impact", "New symptom",
];

export const WEATHER_OPTIONS = ["Cold", "Hot", "Humid", "Rain / storm", "Pressure change", "Dry"];

export const ACTIVITY_OPTIONS = [
  "Sedentary", "Light", "Moderate", "Heavy / strenuous",
  "PT / running", "Prolonged standing", "Prolonged walking", "Lifting",
];

export const HELPED_OPTIONS = ["Helped", "Helped some", "No relief", "Made worse"];

// VA-claim-relevant functional impact chips, grouped under one heading in the
// entry form: "What did it prevent you from doing?"
export const VA_IMPACT_OPTIONS = [
  "Missed work", "Missed PT", "Missed duty",
  "Could not stand > 30 min", "Could not walk normal distance",
  "Could not sleep", "Could not concentrate", "Avoided people",
  "Needed help", "Needed medication", "Needed brace/cane", "Went to urgent care",
  "Stayed in bed", "Could not drive", "Could not complete normal chores",
];

// Structured duration quick-buttons (free text still allowed via Custom).
export const DURATION_PRESETS = ["15 min", "30 min", "1 hr", "2-4 hrs", "Half day", "All day", "Multiple days"];

// ---- Unified trigger + treatment vocabularies (Symptom Tracker v4) -------------
// One combined chip cloud replaces the old separate Triggers / Weather / Activity
// / Diet / Work-schedule sections. Detailed factors still live behind the
// "Advanced factors" accordion via the existing FactorsSection.
export const UNIFIED_TRIGGER_CHIPS = [
  "Poor sleep", "Stress", "Cold weather", "Heat", "Humidity", "Rain / pressure change",
  "PT / running", "Prolonged standing", "Prolonged walking", "Lifting", "Sitting > 30 min",
  "Red meat", "Alcohol", "Missed medication",
  "Night shift", "Long workday", "Loud noise", "Crowds", "Screen time",
];

// Unified non-Rx treatment / coping chips. Rx chips are merged in dynamically
// from the veteran's prescriptions inside the section.
export const UNIFIED_TREATMENT_CHIPS = [
  "Rest", "Ice", "Heat", "Brace / support", "Cane / crutches", "CPAP",
  "Physical therapy", "Provider visit", "ER / urgent care",
  "Meditation", "Deep breathing",
];

// "Did it help?" answers for the single overall question after picking
// medication / treatment. Maps onto medsTaken[].helped semantics.
export const TREATMENT_HELPED_OPTIONS = ["Helped", "Somewhat helped", "Did not help", "Not sure"];

// Condition-priority lists: chips for each condition type that bubble to the
// top of the unified chip cloud. The full chip list is always shown — these
// just reorder it. Names must match UNIFIED_* exactly.
const CONDITION_TRIGGER_PRIORITY = {
  gout: ["Red meat", "Alcohol", "Missed medication", "Prolonged standing", "Prolonged walking"],
  musculoskeletal: [
    "PT / running", "Lifting", "Prolonged standing", "Prolonged walking",
    "Sitting > 30 min", "Cold weather",
  ],
  mental: ["Stress", "Poor sleep", "Crowds", "Loud noise", "Long workday", "Night shift"],
  tinnitus: ["Loud noise", "Poor sleep", "Stress"],
};

const CONDITION_TREATMENT_PRIORITY = {
  gout: ["Rest", "Ice", "Provider visit"],
  musculoskeletal: ["Rest", "Ice", "Heat", "Brace / support", "Physical therapy", "Cane / crutches"],
  mental: ["Meditation", "Deep breathing", "Provider visit"],
  tinnitus: ["Meditation", "Deep breathing"],
};

/**
 * Reorder a chip list so condition-priority chips appear first while keeping
 * the rest in their original order. `conditionType` comes from
 * `classifyConditionType()`. Unknown types return the list unchanged.
 */
export function prioritizeChips(chips, conditionType, kind = "triggers") {
  const map = kind === "treatments" ? CONDITION_TREATMENT_PRIORITY : CONDITION_TRIGGER_PRIORITY;
  const priority = map[conditionType];
  if (!priority || !priority.length) return chips;
  const prioritySet = new Set(priority);
  const head = priority.filter((p) => chips.includes(p));
  const tail = chips.filter((c) => !prioritySet.has(c));
  return [...head, ...tail];
}

// ---- Medication name normalization + prescription chip dedupe ------------------

const MED_SPELL_FIX = {
  trazadone: "trazodone", trazedone: "trazodone",
  ibuprophen: "ibuprofen", ibuprofin: "ibuprofen",
  allupurinol: "allopurinol", alopurinol: "allopurinol", allopurinal: "allopurinol",
  indomethicin: "indomethacin", indomethecin: "indomethacin", indomethacine: "indomethacin",
  naproxin: "naproxen", naproxine: "naproxen",
  tylanol: "tylenol", tylonol: "tylenol",
  gabapentine: "gabapentin", gabapenton: "gabapentin",
  melatonine: "melatonin",
  sertaline: "sertraline", sertralene: "sertraline",
  seroquil: "seroquel",
  prasozin: "prazosin", prazocin: "prazosin",
  meloxicam: "meloxicam", colchicene: "colchicine",
  cymbolta: "cymbalta", duloxetene: "duloxetine",
};

function titleCaseWord(w) {
  return w ? w[0].toUpperCase() + w.slice(1) : w;
}

/** Split "allopurinol 100MG tablets" → { base: "Allopurinol", strength: "100 mg" }. */
export function splitMedNameStrength(raw) {
  const s = String(raw || "").replace(/\s+/g, " ").trim();
  if (!s) return { base: "", strength: "" };
  const m = s.match(/^(.+?)[\s,]+(\d+(?:[.,]\d+)?\s*(?:mg|mcg|g|ml|iu|units?)\b.*)$/i);
  let base = (m ? m[1] : s).trim();
  let strength = (m ? m[2] : "").trim();
  base = base
    .split(" ")
    .map((w) => titleCaseWord(MED_SPELL_FIX[w.toLowerCase()] || w.toLowerCase()))
    .join(" ");
  if (strength) {
    strength = strength
      .toLowerCase()
      .replace(/(\d)(mg|mcg|g|ml|iu)\b/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();
  }
  return { base, strength };
}

/** Preferred chip format: "Trazodone 100 mg" (spelling-fixed, normalized strength). */
export function formatMedLabel(name, strength = "") {
  const p = splitMedNameStrength(name);
  const st = String(strength || p.strength || "")
    .toLowerCase()
    .replace(/(\d)(mg|mcg|g|ml|iu)\b/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  return st ? `${p.base} ${st}` : p.base;
}

/**
 * Collapse a prescription list into unique medication chips. "Allopurinol" and
 * "allopurinol 100 mg" merge into one chip; the variant with a strength wins
 * the label. Returns [{ key, base, strength, label, prescriptionIds }].
 */
export function dedupePrescriptions(prescriptions = []) {
  const map = new Map();
  prescriptions.filter((p) => !p.archived).forEach((p) => {
    const { base, strength: nameStrength } = splitMedNameStrength(p.medName);
    if (!base) return;
    const key = base.toLowerCase();
    const strength = String(p.strength || nameStrength || "").trim();
    const existing = map.get(key);
    if (existing) {
      existing.prescriptionIds.push(p.id);
      if (!existing.strength && strength) {
        existing.strength = strength;
        existing.label = formatMedLabel(base, strength);
      }
    } else {
      map.set(key, { key, base, strength, label: formatMedLabel(base, strength), prescriptionIds: [p.id] });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.base.localeCompare(b.base));
}

// ---- Condition-adaptive trackers -----------------------------------------------

const MENTAL_RE = /ptsd|depress|anxiet|panic|adjustment disorder|bipolar|\bmst\b|insomnia|mood disorder|trauma|schizo|ocd|persistent depressive/i;
const MSK_RE = /knee|back|shoulder|hip|ankle|neck|spine|spinal|joint|strain|sprain|arthritis|lumbar|cervical|thoracic|wrist|elbow|foot|plantar|rotator|meniscus|radiculopathy|sciatica|degenerative disc|carpal|patell/i;

/**
 * Classify a condition (issue object or plain name string) into one of the
 * condition-specific tracker types. Gout and tinnitus are matched by name
 * first because they live inside broader body systems.
 */
export function classifyConditionType(issueOrName) {
  const name = typeof issueOrName === "string"
    ? issueOrName
    : `${issueOrName?.claimedAs || ""} ${issueOrName?.name || ""}`;
  const system = typeof issueOrName === "object" && issueOrName ? (issueOrName.system || "") : "";
  const n = String(name || "").toLowerCase();
  if (/gout/.test(n)) return "gout";
  if (/tinnitus|ringing in (the )?ear/.test(n)) return "tinnitus";
  if (system === "mental" || MENTAL_RE.test(n)) return "mental";
  if (system === "musculoskeletal" || MSK_RE.test(n)) return "musculoskeletal";
  return "general";
}

// Field kinds: scale (0-10) | count | select | toggle | chips | text
export const CONDITION_TRACKERS = {
  mental: {
    type: "mental",
    label: "Mental health tracker",
    hint: "These map to what a C&P examiner asks about occupational and social impairment.",
    fields: [
      { id: "anxiety", label: "Anxiety", kind: "scale" },
      { id: "depression", label: "Depression", kind: "scale" },
      { id: "irritability", label: "Irritability", kind: "scale" },
      { id: "panicEpisodes", label: "Panic episodes today", kind: "count" },
      { id: "nightmares", label: "Nightmares last night", kind: "count" },
      { id: "concentration", label: "Concentration", kind: "select", options: ["No issue", "Mild difficulty", "Moderate difficulty", "Severe difficulty"] },
      { id: "motivation", label: "Motivation", kind: "select", options: ["Normal", "Low", "Very low", "None"] },
      { id: "workReliability", label: "Work reliability", kind: "select", options: ["No issue", "Late", "Left early", "Missed work", "Conflict at work"] },
      { id: "stressTolerance", label: "Stress tolerance", kind: "select", options: ["Normal", "Reduced", "Poor", "Cannot handle stress"] },
      { id: "socialWithdrawal", label: "Avoided people / social withdrawal", kind: "toggle" },
      { id: "relationships", label: "Effect on relationships", kind: "text", placeholder: "e.g., argued with spouse, skipped family dinner" },
    ],
  },
  musculoskeletal: {
    type: "musculoskeletal",
    label: "Musculoskeletal tracker",
    hint: "ROM limits, instability, and repeated-use limits drive musculoskeletal ratings.",
    fields: [
      { id: "romLimit", label: "Range of motion limit", kind: "select", options: ["No limit", "Mild", "Moderate", "Severe", "Locked / unable"] },
      { id: "instability", label: "Instability / giving way", kind: "toggle" },
      { id: "swelling", label: "Swelling", kind: "toggle" },
      { id: "weightBearing", label: "Limited by", kind: "chips", options: ["Walking", "Standing", "Lifting", "Bending", "Kneeling", "Squatting", "Stairs"] },
    ],
  },
  gout: {
    type: "gout",
    label: "Gout flare tracker",
    hint: "Joint, redness, warmth, and diet triggers document flare frequency and severity.",
    suggestedMeds: ["Allopurinol", "Indomethacin", "Colchicine", "Naproxen"],
    fields: [
      { id: "affectedJoint", label: "Affected joint", kind: "select", options: ["Big toe (L)", "Big toe (R)", "Ankle (L)", "Ankle (R)", "Knee (L)", "Knee (R)", "Wrist", "Elbow", "Finger", "Other"] },
      { id: "swelling", label: "Swelling", kind: "toggle" },
      { id: "redness", label: "Redness", kind: "toggle" },
      { id: "warmth", label: "Warmth", kind: "toggle" },
      { id: "mobilityLimits", label: "Walking / standing limits", kind: "chips", options: ["Walking limited", "Standing limited", "Could not bear weight"] },
      { id: "dietTrigger", label: "Diet trigger", kind: "text", placeholder: "e.g., red meat, beer, shellfish (purines)" },
    ],
  },
  tinnitus: {
    type: "tinnitus",
    label: "Tinnitus tracker",
    hint: "Frequency, location, and sleep / concentration impact are what the examiner asks.",
    fields: [
      { id: "intensity", label: "Ringing intensity", kind: "scale" },
      { id: "pattern", label: "Pattern", kind: "select", options: ["Constant", "Intermittent"] },
      { id: "location", label: "Where", kind: "select", options: ["Left ear", "Right ear", "Both ears", "In head"] },
      { id: "sleepImpact", label: "Disrupted sleep", kind: "toggle" },
      { id: "concentrationImpact", label: "Disrupted concentration", kind: "toggle" },
      { id: "quietRoomWorse", label: "Worse in quiet rooms", kind: "toggle" },
      { id: "noiseExposure", label: "Noise exposure notes", kind: "text", placeholder: "e.g., flight line, range day, concert" },
    ],
  },
};

/** Returns the condition-adaptive tracker for an issue/name, or null for general. */
export function getTrackerForIssue(issueOrName) {
  const type = classifyConditionType(issueOrName);
  return CONDITION_TRACKERS[type] || null;
}

// ---- Durations -----------------------------------------------------------------

function unitToHours(u) {
  const s = String(u || "").toLowerCase();
  if (s.startsWith("min") || s === "m") return 1 / 60;
  if (s.startsWith("h")) return 1;
  if (s.startsWith("d")) return 24;
  if (s.startsWith("w")) return 24 * 7;
  return null;
}

/** Parse "2 hours" / "30 min" / "all day" / "2-3 hrs" / "3 days" → hours, else null. */
export function parseDurationToHours(str) {
  const s = String(str || "").toLowerCase().trim();
  if (!s) return null;
  if (/multiple\s*days|several\s*days/.test(s)) return 48;
  if (/half\s*(a\s*)?day/.test(s)) return 12;
  if (/all\s*day/.test(s)) return 24;
  let m = s.match(/(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)\s*(minutes?|mins?|hours?|hrs?|h\b|days?|d\b|weeks?|wks?|w\b)/);
  if (m) {
    const u = unitToHours(m[3]);
    if (u != null) return round2(((Number(m[1]) + Number(m[2])) / 2) * u);
  }
  m = s.match(/(\d+(?:\.\d+)?)\s*(minutes?|mins?|m\b|hours?|hrs?|h\b|days?|d\b|weeks?|wks?|w\b)/);
  if (m) {
    const u = unitToHours(m[2]);
    if (u != null) return round2(Number(m[1]) * u);
  }
  return null;
}

/** Duration of a flare entry in hours — prefers endDate-date, falls back to duration text. */
export function flareDurationHours(entry) {
  if (entry?.date && entry?.endDate) {
    const a = new Date(entry.date);
    const b = new Date(entry.endDate);
    if (!Number.isNaN(a.getTime()) && !Number.isNaN(b.getTime())) {
      const h = (b - a) / 3600000;
      if (h > 0 && h < 24 * 90) return round1(h);
    }
  }
  return parseDurationToHours(entry?.duration);
}

export function fmtHours(h) {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${round1(h)} hr${round1(h) === 1 ? "" : "s"}`;
  return `${round1(h / 24)} days`;
}

// ---- Insights ------------------------------------------------------------------

function countList(entries, getter) {
  const counts = {};
  entries.forEach((e) => {
    const vals = getter(e) || [];
    (Array.isArray(vals) ? vals : [vals]).forEach((v) => {
      const k = String(v || "").trim();
      if (k) counts[k] = (counts[k] || 0) + 1;
    });
  });
  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

/**
 * Compute the VA-focused insight pack from raw entries.
 * Options: { now, months (flare buckets), trendDays }
 */
export function computeInsights(items, { now = new Date(), months = 6, trendDays = 60 } = {}) {
  const entries = (items || []).filter((e) => e.date && !Number.isNaN(new Date(e.date).getTime()));
  const flares = entries.filter((e) => e.entryType === "flare");

  // Flare frequency by month (last N calendar months)
  const flaresByMonth = [];
  const byKey = new Map();
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const row = { key, label: d.toLocaleDateString("en-US", { month: "short" }), count: 0 };
    flaresByMonth.push(row);
    byKey.set(key, row);
  }
  flares.forEach((e) => {
    const d = new Date(e.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const row = byKey.get(key);
    if (row) row.count += 1;
  });

  // Average flare duration
  const durations = flares.map(flareDurationHours).filter((h) => h != null && h > 0);
  const avgFlareDurationHours = durations.length
    ? round1(durations.reduce((a, b) => a + b, 0) / durations.length)
    : null;

  const topTriggers = countList(entries, (e) => e.triggers);

  // Medication effectiveness (from medsTaken[{name, helped}])
  const medMap = new Map();
  entries.forEach((e) => (e.medsTaken || []).forEach((m) => {
    const name = String(m?.name || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    const rec = medMap.get(key) || { name, taken: 0, helped: 0, some: 0, none: 0, worse: 0 };
    rec.taken += 1;
    if (m.helped === "Helped") rec.helped += 1;
    else if (m.helped === "Helped some") rec.some += 1;
    else if (m.helped === "No relief") rec.none += 1;
    else if (m.helped === "Made worse") rec.worse += 1;
    medMap.set(key, rec);
  }));
  const medEffectiveness = Array.from(medMap.values())
    .map((r) => ({ ...r, score: r.taken ? round2((r.helped + 0.5 * r.some) / r.taken) : 0 }))
    .sort((a, b) => b.taken - a.taken || b.score - a.score);

  // Sleep quality vs severity
  const sleepBuckets = [
    { id: "poor", label: "Poor sleep (0-4)", sevs: [] },
    { id: "fair", label: "Fair sleep (5-7)", sevs: [] },
    { id: "good", label: "Good sleep (8-10)", sevs: [] },
  ];
  entries.forEach((e) => {
    const q = e.factors?.sleepQuality;
    if (q == null || q === "") return;
    const sev = Number(e.severity || 0);
    const b = q <= 4 ? sleepBuckets[0] : q <= 7 ? sleepBuckets[1] : sleepBuckets[2];
    b.sevs.push(sev);
  });
  const sleepVsSeverity = sleepBuckets.map((b) => ({
    id: b.id,
    label: b.label,
    n: b.sevs.length,
    avgSeverity: b.sevs.length ? round1(b.sevs.reduce((a, v) => a + v, 0) / b.sevs.length) : null,
  }));

  // Stress level vs severity
  const stressBuckets = [
    { id: "low", label: "Low stress (0-3)", sevs: [] },
    { id: "moderate", label: "Moderate stress (4-6)", sevs: [] },
    { id: "high", label: "High stress (7-10)", sevs: [] },
  ];
  entries.forEach((e) => {
    const q = e.factors?.stressLevel;
    if (q == null || q === "") return;
    const sev = Number(e.severity || 0);
    const b = q <= 3 ? stressBuckets[0] : q <= 6 ? stressBuckets[1] : stressBuckets[2];
    b.sevs.push(sev);
  });
  const stressVsSeverity = stressBuckets.map((b) => ({
    id: b.id,
    label: b.label,
    n: b.sevs.length,
    avgSeverity: b.sevs.length ? round1(b.sevs.reduce((a, v) => a + v, 0) / b.sevs.length) : null,
  }));

  // Most affected body parts (body regions + condition-tracker joints)
  const bodyPartCounts = countList(entries, (e) => [
    ...(e.bodyRegions || []),
    ...(e.conditionFields?.affectedJoint ? [e.conditionFields.affectedJoint] : []),
  ]);

  // Missed work / PT / duty
  const missedByActivity = {};
  let missedDaysTotal = 0;
  entries.forEach((e) => {
    (e.missedActivities || []).forEach((a) => { missedByActivity[a] = (missedByActivity[a] || 0) + 1; });
    if (e.missedDays != null && e.missedDays !== "") missedDaysTotal += Number(e.missedDays) || 0;
  });
  missedDaysTotal = round1(missedDaysTotal);

  // Worst days (worstDay flag wins, then severity, then recency)
  const worstDays = entries
    .slice()
    .sort((a, b) => (
      (b.worstDay ? 1 : 0) - (a.worstDay ? 1 : 0)
      || (Number(b.severity) || 0) - (Number(a.severity) || 0)
      || new Date(b.date) - new Date(a.date)
    ))
    .slice(0, 5)
    .map((e) => ({ date: e.date, severity: Number(e.severity) || 0, symptom: e.symptom || "", worstDay: !!e.worstDay }));

  // Trend — least-squares slope of daily avg severity over the trend window
  const start = new Date(now);
  start.setDate(start.getDate() - (trendDays - 1));
  start.setHours(0, 0, 0, 0);
  const dayMap = new Map();
  entries.forEach((e) => {
    const d = new Date(e.date);
    if (d < start || d > now) return;
    const idx = Math.floor((d - start) / 86400000);
    if (!dayMap.has(idx)) dayMap.set(idx, []);
    dayMap.get(idx).push(Number(e.severity || 0));
  });
  const pts = Array.from(dayMap.entries()).map(([x, arr]) => ({ x, y: arr.reduce((a, v) => a + v, 0) / arr.length }));
  let trend = { direction: "insufficient", slopePerWeek: 0, daysWithData: pts.length };
  if (pts.length >= 5) {
    const n = pts.length;
    const sx = pts.reduce((a, p) => a + p.x, 0);
    const sy = pts.reduce((a, p) => a + p.y, 0);
    const sxx = pts.reduce((a, p) => a + p.x * p.x, 0);
    const sxy = pts.reduce((a, p) => a + p.x * p.y, 0);
    const denom = n * sxx - sx * sx;
    const slope = denom !== 0 ? (n * sxy - sx * sy) / denom : 0;
    const slopePerWeek = round2(slope * 7);
    let direction = "stable";
    if (slopePerWeek > 0.15) direction = "worsening";
    else if (slopePerWeek < -0.15) direction = "improving";
    trend = { direction, slopePerWeek, daysWithData: pts.length };
  }

  return {
    entryCount: entries.length,
    flareCount: flares.length,
    flaresByMonth,
    flaresThisMonth: flaresByMonth.length ? flaresByMonth[flaresByMonth.length - 1].count : 0,
    avgFlareDurationHours,
    topTriggers,
    medEffectiveness,
    sleepVsSeverity,
    stressVsSeverity,
    bodyPartCounts,
    missedByActivity,
    missedDaysTotal,
    worstDays,
    trend,
  };
}

// ---- Grouping ------------------------------------------------------------------

/** Group entries per condition: claimed → undiagnosed → general (same keys as Symptom Log PDF). */
export function groupEntriesByCondition(items, issuesById = {}) {
  const map = new Map();
  (items || []).forEach((e) => {
    let key;
    if (e.linkedIssueId) key = e.linkedIssueId;
    else if (e.isUndiagnosed) key = `__undx__${(e.suspectedCondition || "").toLowerCase().trim()}`;
    else key = "__general__";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(e);
  });
  const groups = [];
  map.forEach((entries, key) => {
    let name;
    let isUndx = false;
    if (key.startsWith("__undx__")) {
      const guess = entries[0].suspectedCondition;
      name = guess ? `Undiagnosed: ${guess}` : "Undiagnosed symptoms";
      isUndx = true;
    } else if (key === "__general__") {
      name = "General / unspecified";
    } else {
      name = issuesById[key]?.claimedAs || "Linked condition";
    }
    groups.push({ key, name, isUndx, entries });
  });
  groups.sort((a, b) => {
    const tier = (g) => (g.key === "__general__" ? 2 : g.isUndx ? 1 : 0);
    return tier(a) - tier(b) || b.entries.length - a.entries.length;
  });
  return groups;
}

// ---- Evidence summary ------------------------------------------------------------

/**
 * Build the full "Symptom Evidence Summary" data pack for one condition's entries.
 * Deterministic — no AI. Powers both the Evidence Summary PDF and C&P Prep sheet.
 */
export function buildEvidenceSummary({ entries = [], conditionName = "Condition" } = {}) {
  const sorted = entries.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
  const first = sorted[0]?.date;
  const last = sorted[sorted.length - 1]?.date;
  const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
  const dateRangeLabel = first ? (fmtD(first) === fmtD(last) ? fmtD(first) : `${fmtD(first)} - ${fmtD(last)}`) : "";
  const rangeDays = first && last ? Math.max(1, Math.round((new Date(last) - new Date(first)) / 86400000) + 1) : 0;

  const sevs = entries.map((e) => Number(e.severity || 0));
  const flares = entries.filter((e) => e.entryType === "flare");
  const avgSeverity = sevs.length ? round1(sevs.reduce((a, b) => a + b, 0) / sevs.length) : 0;
  const worstSeverity = sevs.length ? Math.max(...sevs) : 0;

  const durations = flares.map(flareDurationHours).filter((h) => h != null && h > 0);
  const avgFlareDurationHours = durations.length
    ? round1(durations.reduce((a, b) => a + b, 0) / durations.length)
    : null;

  const topTriggers = countList(entries, (e) => e.triggers);
  const topReliefs = countList(entries, (e) => e.reliefMethods);
  const topImpacts = countList(entries, (e) => e.impact);
  const topSymptoms = countList(entries, (e) => (e.symptom ? [e.symptom] : []));
  const treatmentsUsed = countList(entries, (e) => e.treatments);
  const assistiveDevices = countList(entries, (e) => e.assistiveDevices).map((r) => r.label);

  // Meds — aggregate + dominant verdict
  const medMap = new Map();
  entries.forEach((e) => (e.medsTaken || []).forEach((m) => {
    const name = String(m?.name || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    const rec = medMap.get(key) || { name, taken: 0, verdicts: {} };
    rec.taken += 1;
    if (m.helped) rec.verdicts[m.helped] = (rec.verdicts[m.helped] || 0) + 1;
    medMap.set(key, rec);
  }));
  const medsUsed = Array.from(medMap.values())
    .map((r) => {
      const top = Object.entries(r.verdicts).sort((a, b) => b[1] - a[1])[0];
      return { name: r.name, taken: r.taken, helpedLabel: top ? top[0] : "" };
    })
    .sort((a, b) => b.taken - a.taken);

  const copingNoted = Array.from(new Set(
    entries.map((e) => String(e.copingUsed || "").trim()).filter(Boolean),
  )).slice(0, 3);

  const missedByActivity = {};
  let missedDaysTotal = 0;
  entries.forEach((e) => {
    (e.missedActivities || []).forEach((a) => { missedByActivity[a] = (missedByActivity[a] || 0) + 1; });
    if (e.missedDays != null && e.missedDays !== "") missedDaysTotal += Number(e.missedDays) || 0;
  });
  missedDaysTotal = round1(missedDaysTotal);

  const worstDayExamples = entries
    .slice()
    .sort((a, b) => (
      (b.worstDay ? 1 : 0) - (a.worstDay ? 1 : 0)
      || (Number(b.severity) || 0) - (Number(a.severity) || 0)
      || new Date(b.date) - new Date(a.date)
    ))
    .slice(0, 3)
    .map((e) => ({
      date: e.date,
      severity: Number(e.severity) || 0,
      symptom: e.symptom || "",
      notes: String(e.notes || "").slice(0, 240),
    }));

  const repeatedUseNotes = entries
    .filter((e) => String(e.repeatedUseImpact || "").trim())
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3)
    .map((e) => ({ date: e.date, text: String(e.repeatedUseImpact).slice(0, 240) }));

  const notesExcerpts = entries
    .filter((e) => String(e.notes || "").trim())
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5)
    .map((e) => ({ date: e.date, text: String(e.notes).slice(0, 240) }));

  const perWeek = rangeDays > 0 ? round1(entries.length / (rangeDays / 7)) : entries.length;

  // Deterministic appointment-ready paragraph
  const parts = [];
  parts.push(
    `Over ${dateRangeLabel || "the tracking period"}, I logged ${entries.length} symptom entr${entries.length === 1 ? "y" : "ies"} ` +
    `for ${conditionName}, including ${flares.length} flare-up${flares.length === 1 ? "" : "s"}.`,
  );
  if (entries.length) parts.push(`Average severity ${avgSeverity}/10; worst ${worstSeverity}/10.`);
  if (avgFlareDurationHours != null) parts.push(`A typical flare lasts about ${fmtHours(avgFlareDurationHours)}.`);
  if (topTriggers.length) parts.push(`Most common triggers: ${topTriggers.slice(0, 3).map((t) => t.label).join(", ")}.`);
  const helps = [...topReliefs.slice(0, 2).map((r) => r.label), ...medsUsed.slice(0, 2).map((m) => m.name)];
  if (helps.length) parts.push(`What helps: ${helps.join(", ")}.`);
  if (topImpacts.length) parts.push(`Functional impact most often: ${topImpacts.slice(0, 3).map((i) => i.label).join(", ")}.`);
  const missedBits = Object.entries(missedByActivity).map(([k, v]) => `${k} x${v}`);
  if (missedBits.length || missedDaysTotal) {
    parts.push(`Missed: ${missedBits.join(", ")}${missedDaysTotal ? ` (~${missedDaysTotal} day${missedDaysTotal === 1 ? "" : "s"} total)` : ""}.`);
  }
  const appointmentSummary = parts.join(" ");

  return {
    conditionName,
    dateRangeLabel,
    rangeDays,
    entryCount: entries.length,
    flareCount: flares.length,
    avgSeverity,
    worstSeverity,
    avgFlareDurationHours,
    perWeek,
    topTriggers,
    topReliefs,
    topImpacts,
    topSymptoms,
    treatmentsUsed,
    assistiveDevices,
    medsUsed,
    copingNoted,
    missedByActivity,
    missedDaysTotal,
    worstDayExamples,
    repeatedUseNotes,
    notesExcerpts,
    appointmentSummary,
  };
}

// ---- Quick-log helpers ------------------------------------------------------------

/** Copy an entry into a fresh create payload — strips server fields, resets timing. */
export function buildDuplicatePayload(entry, nowLocalValue) {
  const { id, user_id: userId, createdAt, updatedAt, ...rest } = entry || {};
  void id; void userId; void createdAt; void updatedAt;
  return {
    ...rest,
    date: nowLocalValue,
    endDate: "",
    photos: [],
  };
}

// ---- internals ------------------------------------------------------------

function round1(n) { return Math.round(n * 10) / 10; }
function round2(n) { return Math.round(n * 100) / 100; }
